import { lazy, Suspense, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';

// The learner frame is its own chunk: its pages load only when someone opens /learn.
const LearnLayout = lazy(() => import('./LearnLayout'));
const LearnHomePage = lazy(() => import('./pages/LearnHomePage'));
const LearnExplorePage = lazy(() => import('./pages/LearnExplorePage'));
const LearnItemPage = lazy(() => import('./pages/LearnItemPage'));
const LearnMinePage = lazy(() => import('./pages/LearnListPages').then((m) => ({ default: m.LearnMinePage })));
const LearnTranscriptPage = lazy(() => import('./pages/LearnTranscriptPage'));
const LearnNoticesPage = lazy(() => import('./pages/LearnNoticesPage'));
const VerifyCertificatePage = lazy(() => import('./pages/VerifyCertificatePage'));
const LearnSavedPage = lazy(() => import('./pages/LearnListPages').then((m) => ({ default: m.LearnSavedPage })));
const LearnTeamsPage = lazy(() => import('./pages/LearnTeamsPage'));
const LearnSkillsPage = lazy(() => import('./pages/LearnEngagePages').then((m) => ({ default: m.LearnSkillsPage })));
const LearnLeaderboardPage = lazy(() => import('./pages/LearnEngagePages').then((m) => ({ default: m.LearnLeaderboardPage })));
const LearnSurveysPage = lazy(() => import('./pages/LearnEngagePages').then((m) => ({ default: m.LearnSurveysPage })));
const LearnAssistantPage = lazy(() => import('./pages/LearnEngagePages').then((m) => ({ default: m.LearnAssistantPage })));
const LearnDownloadsPage = lazy(() => import('./pages/LearnEngagePages').then((m) => ({ default: m.LearnDownloadsPage })));

const wait = (element: ReactNode) => (
  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>{element}</Suspense>
);

/** The learner frame, inside sign-in but outside the main layout (docs §266). No permission — the module only: every member may learn. */
export const learnRoutes: RouteObject[] = [
  {
    path: 'learn',
    element: wait(<RequireModule module={Modules.Learning}><LearnLayout /></RequireModule>),
    children: [
      { index: true, element: wait(<LearnHomePage />) },
      { path: 'explore', element: wait(<LearnExplorePage />) },
      { path: 'items/:id', element: wait(<LearnItemPage />) },
      { path: 'mine', element: wait(<LearnMinePage />) },
      { path: 'saved', element: wait(<LearnSavedPage />) },
      { path: 'transcript', element: wait(<LearnTranscriptPage />) },
      { path: 'notifications', element: wait(<LearnNoticesPage />) },
      // Engagement, skills, offline and the assistant (docs §274).
      { path: 'skills', element: wait(<LearnSkillsPage />) },
      { path: 'leaderboard', element: wait(<LearnLeaderboardPage />) },
      { path: 'surveys', element: wait(<LearnSurveysPage />) },
      { path: 'surveys/:id', element: wait(<LearnSurveysPage />) },
      { path: 'assistant', element: wait(<LearnAssistantPage />) },
      { path: 'downloads', element: wait(<LearnDownloadsPage />) },
    ],
  },
  // The Microsoft Teams personal tab (docs §273): no frame around it.
  { path: 'learn/teams', element: wait(<RequireModule module={Modules.Learning}><LearnTeamsPage /></RequireModule>) },
];

/** The anonymous page a certificate's QR code opens (docs §269). */
export const learnPublicRoutes: RouteObject[] = [
  { path: '/verify-certificate/:code?', element: wait(<VerifyCertificatePage />) },
];
